import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import 'dayjs/locale/pt';
import DateFnsUtils from '@date-io/date-fns';
import deLocale from 'date-fns/locale/pt-BR';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { Chip, TextField } from '@mui/material';
import BrazilMoney from 'utils/BrazilMoney';
import SalesService from 'services/SalesService';

export function useSales() {
  const location = useLocation();
  const [sales, setSales] = useState([]);
  const [openProgress, setOpenProgress] = useState(false);
  const [exportData, setExportData] = useState();
  const [errors, setErrors] = useState([]);
  const nameProperty = useSelector((state) => state.nameProperty.nameProperty);

  const columns = [
    {
      title: 'Data',
      field: 'date',
      type: 'date',
      editable: 'never',
      hideFilterIcon: true,
      dateSetting: { locale: 'pt-BR' },
      editComponent: (props) => (
        <LocalizationProvider adapterLocale={deLocale} dateAdapter={DateFnsUtils}>
          <DatePicker
            format="dd/MM/yyyy"
            value={props.value}
            onChange={props.onChange}
            InputProps={{
              style: {
                fontSize: 13,
              },
            }}
            fullWidth
            // inputFormat="DD/MM/YYYY"
            renderInput={(params) => <TextField {...params} />}
          />
        </LocalizationProvider>
      ),
    },
    {
      title: 'Cliente',
      field: 'stakeholder.name',
      hideFilterIcon: true,
      editable: 'never',
    },
    {
      title: 'Status',
      field: 'status',
      type: 'string',
      hideFilterIcon: true,
      editable: 'never',
      render: (rowData) => (
        <Chip
          style={{
            backgroundColor: rowData.status === 'pendente' ? '#F44336' : '#0B8846',
            color: '#fff',
            fontWeight: 'bolder',
          }}
          label={rowData.status[0].toUpperCase() + rowData.status.substr(1)}
        />
      ),
    },
    {
      title: 'Quantidade (Kg)',
      field: 'quantity',
      hideFilterIcon: true,
      render: (rowData) => (
        <span>{`${new Intl.NumberFormat('pt-BR').format(rowData.quantity)} Kg`}</span>
      ),
    },
    {
      title: 'Preço',
      field: 'price',
      hideFilterIcon: true,
      render: (rowData) => (
        <span><BrazilMoney value={rowData.price} /></span>
      ),
    },
  ];

  const columnsForExport = [
    {
      title: 'Data',
      field: 'date',
    },
    {
      title: 'Hora',
      field: 'time',
    },
    {
      title: 'Cliente',
      field: 'stakeholder',
    },
    {
      title: 'Descrição',
      field: 'description',
    },
    {
      title: 'Status',
      field: 'status',
    },
    {
      title: 'Desconto',
      field: 'discount',
    },
    {
      title: 'Quantidade de parcelas',
      field: 'parts_quantity',
    },
    {
      title: 'Quantidade (Kg)',
      field: 'quantity',
    },
    {
      title: 'Preço',
      field: 'price',
    },
  ];

  useEffect(() => {
    async function loadData() {
      try {
        let response;
        if (location.state !== undefined) {
          response = await SalesService.saleListByClientId(location.state.id);
        } else {
          response = await SalesService.saleList();
        }

        if (response.length === 0) {
          setOpenProgress(true);
        }
        if (response) {
          setSales(response);
        }
      } catch (err) {
        setErrors((prev) => [...prev, err.message]);
      } finally {
        setOpenProgress(true);
      }
    }
    loadData();
  }, [location.state, nameProperty]);

  useEffect(() => {
    async function loadSalesExport() {
      try {
        const response = await SalesService.saleListExport();

        if (response.length === 0) {
          setOpenProgress(true);
        }
        if (response) {
          setExportData(response);
        }
      } catch (err) {
        setErrors((prev) => [err.message, ...prev]);
        setOpenProgress(true);
      }
    }
    loadSalesExport();
  }, [nameProperty]);

  return {
    sales,
    openProgress,
    exportData,
    columns,
    columnsForExport,
    location,
    errors,
  };
}
